import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEspecificacao } from '../contexts/EspecificacaoContext';
import { BotaoEspecificacao } from '../components/especificacao/EspecificacaoOverlays';
import Folha from '../components/especificacao/Folha';
import { fmtQtd } from '../utils/especificacao';
import api from '../services/api';
import './Catalogo.css';

const norm = s => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
// Rótulo do balde "sem categoria" nos filtros que não cobrem 100% (cor, acabamento).
// Em vez de esconder quem não foi classificado (o que fazia o filtro mentir sobre o
// tamanho do catálogo), ele vira uma opção EXPLÍCITA — e encolhe conforme classificamos.
// ⚠️ "Outras" é diferente: é classificação REAL (multicolor/estampa), não ausência.
const SEM_CAT = 'Não definido';
// Amostra de cor ao lado de cada família no filtro de Cor
const COR_HEX = {
  Branco: '#ffffff', 'Off-white': '#f3eee2', Bege: '#d8c3a0', 'Cinza claro': '#d2d4d2', 'Cinza médio': '#9b9e9b',
  Grafite: '#4b4f4c', Preto: '#141414', Marrom: '#6b4428', Terracota: '#b9573a', Amadeirado: '#a8764a',
  Azul: '#4170b5', Verde: '#4f8a55', Outras: 'conic-gradient(#d8c3a0, #4170b5, #b9573a, #4f8a55, #d8c3a0)',
};
const ORDENS = [
  ['relevancia', 'Mais relevantes'], ['estoque', 'Maior estoque'], ['nome', 'Nome (A–Z)'], ['comissao', 'Comissão primeiro'],
];
// Estoque com a unidade do produto: pisos = m², metais/louças = un/pç/etc.
const fmtEstoque = (v, unid) => {
  const n = Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
  const u = String(unid || '').toUpperCase() === 'M2' ? 'm²' : String(unid || 'un').toLowerCase();
  return `${n} ${u}`;
};
const temHover = () => typeof window !== 'undefined' && window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;

/**
 * Bloco de filtro da coluna lateral (e da folha de filtros no celular), estilo
 * loja online: título recolhível, opções com contagem, "ver mais" e busca nas
 * listas grandes. A contagem é CRUZADA: mostra quantos produtos sobram
 * combinando com os outros filtros já marcados. Opção zerada some (a não ser
 * que esteja marcada). A ordem é fixa (mais produtos no departamento primeiro),
 * pra lista não "pular" enquanto a pessoa clica.
 */
function Faceta({ titulo, opcoes, selecionados, onToggle, onLimpar, contagem, rotulo = op => op, desenho, buscavel, limite = 7, aviso }) {
  const [aberta, setAberta] = useState(true);
  const [todas, setTodas] = useState(false);
  const [q, setQ] = useState('');
  const visiveis = opcoes.filter(op => selecionados.includes(op) || (contagem.get(op) || 0) > 0);
  if (!visiveis.length) return null;
  const termo = norm(q);
  const filtradas = termo ? visiveis.filter(op => norm(rotulo(op)).includes(termo)) : visiveis;
  let mostrar = filtradas;
  if (!todas && !termo && filtradas.length > limite) {
    const primeiras = filtradas.slice(0, limite);
    mostrar = [...primeiras, ...filtradas.filter(op => selecionados.includes(op) && !primeiras.includes(op))];
  }
  const n = selecionados.length;
  return (
    <div className="fac">
      <button type="button" className="fac-cab" onClick={() => setAberta(a => !a)} aria-expanded={aberta}>
        <span>{titulo}{n > 0 && <em>{n}</em>}</span>
        <i className={`bi bi-chevron-${aberta ? 'up' : 'down'}`} />
      </button>
      {aberta && (
        <div className="fac-corpo">
          {aviso && <div className="fac-aviso"><i className="bi bi-info-circle" />{aviso}</div>}
          {buscavel && visiveis.length > 8 && (
            <div className="fac-busca">
              <i className="bi bi-search" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder={`Buscar ${titulo.toLowerCase()}...`} />
            </div>
          )}
          <ul>
            {mostrar.map(op => (
              <li key={op}>
                <label className={`fac-op ${selecionados.includes(op) ? 'on' : ''}`}>
                  <input type="checkbox" checked={selecionados.includes(op)} onChange={() => onToggle(op)} />
                  <span className="fac-op-txt">{desenho ? desenho(op) : rotulo(op)}</span>
                  <span className="fac-cont">{contagem.get(op) || 0}</span>
                </label>
              </li>
            ))}
            {termo && !filtradas.length && <li className="fac-nada">Nada encontrado</li>}
          </ul>
          <div className="fac-pe">
            {!termo && filtradas.length > limite && (
              <button type="button" className="fac-mais" onClick={() => setTodas(t => !t)}>
                {todas ? 'Ver menos' : `Ver mais (${filtradas.length - limite})`}
              </button>
            )}
            {n > 0 && <button type="button" className="fac-limpar" onClick={onLimpar}>Limpar</button>}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Card de produto com troca de foto no hover (desktop): a capa é a foto 1 e, se
 * houver uma segunda foto, ela aparece por cima com fade quando o mouse passa.
 * "+ Especificar" adiciona na especificação ativa; se o produto já está nela,
 * o botão mostra a quantidade (e toque soma mais).
 */
function CatalogoCard({ produto, apiUrl, onAbrir, onEspecificar, naEsp }) {
  // Alguns produtos têm foto cadastrada no ERP mas o arquivo físico não existe
  // (404): cai no placeholder em vez da imagem quebrada do navegador.
  const [capaErro, setCapaErro] = useState(false);
  const [segErro, setSegErro] = useState(false);
  const fotos = produto.fotos || [];
  const isEncomenda = produto.disponibilidade === 'Encomenda';
  // "Tem estoque" pelo valor EXIBIDO (arredondado): resíduos tipo 0,02 m² contam como 0
  const temEstoque = Math.round(produto.estoqueGrupo) > 0;
  const temCapa = !!fotos[0] && !capaErro;
  const temSegunda = temCapa && !!fotos[1] && !segErro;

  return (
    <div className="cat-card" onClick={onAbrir} role="button" tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' && e.target === e.currentTarget) onAbrir(); }}>
      <div className="cat-card-img">
        {!temCapa && <div className="cat-img-ph"><i className="bi bi-box-seam" /></div>}
        {fotos[0] && !capaErro && (
          <img className="cat-img-1" src={`${apiUrl}${fotos[0]}`} alt={produto.nome}
            loading="lazy" onError={() => setCapaErro(true)} />
        )}
        {temCapa && fotos[1] && !segErro && (
          <img className="cat-img-2" src={`${apiUrl}${fotos[1]}`} alt="" aria-hidden="true"
            loading="lazy" onError={() => setSegErro(true)} />
        )}
        {temSegunda && (
          <span className="cat-fotos-tag"><i className="bi bi-images" />{fotos.length}</span>
        )}
        {/* O mix inteiro aparece; só quem paga comissão leva o símbolo (legenda no
            cabeçalho) — marcar também quem NÃO paga só poluía o card */}
        {produto.pagaComissao && (
          <span className="cat-comissao-tag" title="Paga comissão por indicação"><i className="bi bi-cash-coin" /></span>
        )}
      </div>
      <div className="cat-card-body">
        <div className="cat-tipo">{produto.tipo}</div>
        <div className="cat-nome">{produto.nome}</div>
        <div className="cat-cod">{produto.fornecedor} · Cód {produto.codpro}</div>
        <div className="cat-badges">
          <span className={`cat-badge ${isEncomenda ? 'cat-badge-encomenda' : 'cat-badge-linha'}`}>
            <i className={`bi ${isEncomenda ? 'bi-clock-history' : 'bi-check-circle'}`} />
            {produto.disponibilidade}
          </span>
          {temEstoque ? (
            <span className="cat-badge cat-badge-estoque">
              <i className="bi bi-box-seam" />{fmtEstoque(produto.estoqueGrupo, produto.unid)}
            </span>
          ) : (
            <span className="cat-badge cat-badge-sem">
              <i className="bi bi-dash-circle" />sem estoque
            </span>
          )}
        </div>
        {onEspecificar && (
          <button type="button" className={`cat-espec ${naEsp ? 'na' : ''}`}
            onClick={e => { e.stopPropagation(); onEspecificar(); }}
            onKeyDown={e => e.stopPropagation()}
            title={naEsp ? 'Já está na especificação — toque pra somar mais' : 'Adicionar à especificação do projeto'}>
            {naEsp
              ? <><i className="bi bi-check2-circle" /><span className="cat-espec-rot">Especificado · </span>{fmtQtd(naEsp.quantidade, naEsp.unidade)}</>
              : <><i className="bi bi-plus-lg" />Especificar</>}
          </button>
        )}
      </div>
    </div>
  );
}

export default function Catalogo() {
  const { apiUrl } = useAuth();
  const esp = useEspecificacao();
  const navigate = useNavigate();
  // Estado salvo na sessão (filtros + página + scroll), capturado UMA vez no mount —
  // pra "voltar" do detalhe restaurar tudo em vez de zerar a tela.
  const salvoRef = useRef();
  if (!salvoRef.current) {
    try { salvoRef.current = JSON.parse(sessionStorage.getItem('catalogo_estado') || '{}'); }
    catch { salvoRef.current = {}; }
  }
  const salvo = salvoRef.current;
  const [cat, setCat] = useState(null);
  const [categoria, setCategoria] = useState(salvo.categoria || '0101'); // departamento (aba)
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [busca, setBusca] = useState(salvo.busca || '');
  // Navegação pelos níveis do ERP dentro do departamento: categoria (N2) e
  // subcategoria (N3). Guardam CÓDIGOS de classe; o nome vem da árvore.
  const [n2Sel, setN2Sel] = useState(salvo.n2Sel || '');
  const [subSel, setSubSel] = useState(salvo.subSel || []);
  const [forneSel, setForneSel] = useState(salvo.forneSel || []);
  const [formatoSel, setFormatoSel] = useState(salvo.formatoSel || []);
  const [dispSel, setDispSel] = useState(salvo.dispSel || []);
  const [corSel, setCorSel] = useState(salvo.corSel || []);
  const [acabSel, setAcabSel] = useState(salvo.acabSel || []);
  const [soComEstoque, setSoComEstoque] = useState(salvo.soComEstoque || false);
  const [soComissao, setSoComissao] = useState(salvo.soComissao || false);
  const [ordem, setOrdem] = useState(salvo.ordem || 'relevancia');
  const [pagina, setPagina] = useState(salvo.pagina || 1); // paginação numerada
  const [folhaFiltros, setFolhaFiltros] = useState(false); // filtros no celular
  const [menuAberto, setMenuAberto] = useState(null);      // mega-menu (departamento sob o mouse)
  const menuTimer = useRef(null);
  const [folhaDeps, setFolhaDeps] = useState(false);       // lista de departamentos no celular
  const [mostrarHeader, setMostrarHeader] = useState(true);
  // Altura do topo fixo → a coluna de filtros gruda logo abaixo dele
  const topoRef = useRef(null);
  const [topoH, setTopoH] = useState(120);
  useEffect(() => {
    const el = topoRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setTopoH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [scrollY, setScrollY] = useState(0);

  const toggleEm = (setter) => (op) =>
    setter(s => s.includes(op) ? s.filter(x => x !== op) : [...s, op]);

  // Filtros "de refinamento" (a categoria N2 e a busca são navegação, contam à parte)
  const filtrosAtivos = subSel.length + forneSel.length + formatoSel.length + dispSel.length + corSel.length + acabSel.length + (soComEstoque ? 1 : 0) + (soComissao ? 1 : 0);
  const limparFiltros = () => { setSubSel([]); setForneSel([]); setFormatoSel([]); setDispSel([]); setCorSel([]); setAcabSel([]); setSoComEstoque(false); setSoComissao(false); };
  const limparTudo = () => { limparFiltros(); setN2Sel(''); setBusca(''); };

  // Header inteligente: some ao rolar pra baixo, reaparece ao rolar pra cima.
  useEffect(() => {
    let ultimo = window.scrollY;
    const onScroll = () => {
      const atual = window.scrollY;
      if (atual < 90) setMostrarHeader(true);            // perto do topo: sempre visível
      else if (atual > ultimo + 6) { setMostrarHeader(false); setMenuAberto(null); } // rolando pra baixo
      else if (atual < ultimo - 6) setMostrarHeader(true);  // rolando pra cima
      setScrollY(atual);
      ultimo = atual;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setCarregando(true);
    setErro('');
    api.get(`/catalogo?cat=${categoria}`)
      .then(r => setCat(r.data))
      .catch(e => setErro(e.response?.data?.error || 'Erro ao carregar o catálogo'))
      .finally(() => setCarregando(false));
  }, [categoria]);

  // Ir pra um departamento / categoria / subcategoria (mega-menu, chips, trilha).
  // Trocar de departamento zera os filtros (um filtro de pisos não serve em metais).
  // "Só que paga comissão" é preferência do arquiteto: atravessa departamentos.
  const navegar = ({ cat: c, n2 = '', n3 = null }) => {
    setMenuAberto(null);
    if (c && c !== categoria) {
      setBusca(''); setForneSel([]); setFormatoSel([]); setDispSel([]); setCorSel([]); setAcabSel([]); setSoComEstoque(false);
      setCategoria(c);
    }
    setN2Sel(n2);
    setSubSel(n3 ? [n3] : []);
    window.scrollTo({ top: 0 });
  };
  const escolherN2 = (cod) => {
    setN2Sel(cod);
    setSubSel(s => (cod ? s.filter(c => c.startsWith(cod)) : []));
  };

  // Persiste filtros/categoria/página na sessão (preservando o scroll salvo) —
  // pra "voltar" do detalhe restaurar tudo filtrado, sem zerar a tela.
  useEffect(() => {
    let est = {};
    try { est = JSON.parse(sessionStorage.getItem('catalogo_estado') || '{}'); } catch { /* ignora */ }
    sessionStorage.setItem('catalogo_estado', JSON.stringify({
      ...est, categoria, busca, n2Sel, subSel, forneSel, formatoSel, dispSel, corSel, acabSel, soComEstoque, soComissao, ordem, pagina,
    }));
  }, [categoria, busca, n2Sel, subSel, forneSel, formatoSel, dispSel, corSel, acabSel, soComEstoque, soComissao, ordem, pagina]);

  // Abre o detalhe guardando a posição de scroll atual (pra restaurar ao voltar)
  const irProduto = (codpro) => {
    try {
      const est = JSON.parse(sessionStorage.getItem('catalogo_estado') || '{}');
      sessionStorage.setItem('catalogo_estado', JSON.stringify({ ...est, scrollY: window.scrollY }));
    } catch { /* ignora */ }
    navigate(`/catalogo/${codpro}`);
  };

  // Blindagem: seleção salva que não existe mais neste catálogo (sem cores
  // classificadas, classe renomeada/escondida/recomposta pelo gestor) é limpa —
  // senão o filtro some da tela mas continua ativo, esvaziando a lista em silêncio.
  useEffect(() => {
    if (!cat) return;
    if (!(cat.cores || []).length) setCorSel(prev => prev.length ? [] : prev);
    if (!(cat.acabamentos || []).length) setAcabSel(prev => prev.length ? [] : prev);
    const n2s = new Set((cat.arvore || []).map(a => a.cod));
    const n3s = new Set((cat.arvore || []).flatMap(a => a.filhos.map(f => f.cod)));
    setN2Sel(prev => (prev && !n2s.has(prev) ? '' : prev));
    setSubSel(prev => (prev.some(c => !n3s.has(c)) ? prev.filter(c => n3s.has(c)) : prev));
  }, [cat]);

  // Restaura o scroll salvo quando o catálogo termina de carregar (volta do
  // detalhe). A página cresce conforme as imagens lazy carregam, então reaplica
  // o scroll em alguns frames até a altura do documento comportar o alvo.
  const scrollRestaurado = useRef(false);
  useEffect(() => {
    if (!cat || scrollRestaurado.current || !salvo.scrollY) return;
    scrollRestaurado.current = true;
    const alvo = salvo.scrollY;
    let tentativas = 0;
    const tentar = () => {
      window.scrollTo(0, alvo);
      if (++tentativas < 12 && document.body.scrollHeight < alvo + window.innerHeight) {
        setTimeout(tentar, 120);
      }
    };
    requestAnimationFrame(tentar);
  }, [cat]);

  // Setas da barra de departamentos: aparecem quando não cabe tudo; cada uma apaga
  // quando chega na ponta (não depende de trackpad pra rolar de lado)
  const depsRef = useRef(null);
  const [setas, setSetas] = useState({ esq: false, dir: false });
  const medirSetas = useCallback(() => {
    const n = depsRef.current;
    if (!n) return;
    const max = n.scrollWidth - n.clientWidth;
    const novo = { esq: n.scrollLeft > 4, dir: n.scrollLeft < max - 4 };
    setSetas(s => (s.esq === novo.esq && s.dir === novo.dir ? s : novo));
  }, []);
  useEffect(() => {
    const n = depsRef.current;
    if (!n) return undefined;
    medirSetas();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medirSetas) : null;
    ro?.observe(n);
    return () => ro?.disconnect();
  }, [medirSetas, cat]);
  const rolarDeps = (dir) => {
    const n = depsRef.current;
    if (n) n.scrollBy({ left: dir * Math.max(220, n.clientWidth * 0.7), behavior: 'smooth' });
  };

  // Departamento ativo sempre à vista na barra rolável (celular / muitos departamentos)
  useEffect(() => {
    const el = topoRef.current?.querySelector('.loja-dep.ativa');
    const nav = el?.parentElement;
    if (!el || !nav) return;
    const x = el.offsetLeft - nav.offsetLeft; // posição da aba dentro da barra
    if (x < nav.scrollLeft || x + el.offsetWidth > nav.scrollLeft + nav.clientWidth) {
      nav.scrollTo({ left: Math.max(0, x - 4), behavior: 'smooth' });
    }
  }, [cat]);

  // Código de classe → nome amigável (N2 e N3 do departamento atual)
  const nomeClasse = useMemo(() => {
    const m = new Map();
    for (const a of cat?.arvore || []) {
      m.set(a.cod, a.nome);
      for (const f of a.filhos) m.set(f.cod, f.nome);
    }
    return m;
  }, [cat]);
  // Texto de busca de cada produto, montado uma vez por catálogo
  const textoBusca = useMemo(() => new Map((cat?.produtos || []).map(p =>
    [p.codpro, norm(`${p.nome} ${p.codpro} ${p.tipo} ${nomeClasse.get(p.n2) || ''} ${p.fornecedor}`)])), [cat, nomeClasse]);

  // Cada filtro é um teste; `passa(p, exceto)` aplica todos menos um — base da
  // contagem cruzada das facetas (quantos sobram se eu marcar esta opção).
  const termo = norm(busca);
  const testes = useMemo(() => ({
    n2: p => !n2Sel || p.n2 === n2Sel,
    sub: p => !subSel.length || subSel.includes(p.n3),
    forne: p => !forneSel.length || forneSel.includes(p.fornecedor),
    formato: p => !formatoSel.length || formatoSel.includes(p.formato),
    disp: p => !dispSel.length || dispSel.includes(p.disponibilidade),
    cor: p => !corSel.length || corSel.includes(p.cor || SEM_CAT),
    acab: p => !acabSel.length || acabSel.includes(p.acabamento || SEM_CAT),
    estoque: p => !soComEstoque || Math.round(p.estoqueGrupo) > 0,
    comissao: p => !soComissao || p.pagaComissao,
    busca: p => !termo || (textoBusca.get(p.codpro) || '').includes(termo),
  }), [n2Sel, subSel, forneSel, formatoSel, dispSel, corSel, acabSel, soComEstoque, soComissao, termo, textoBusca]);
  const passa = useCallback((p, ...exceto) => {
    for (const k in testes) if (!exceto.includes(k) && !testes[k](p)) return false;
    return true;
  }, [testes]);

  // Filtra + ordena numa lista ÚNICA, já na ordem final de exibição.
  // "Mais relevantes" = fornecedor com mais itens primeiro; dentro, maior estoque.
  const filtradosOrdenados = useMemo(() => {
    const filtrados = (cat?.produtos || []).filter(p => passa(p));
    const porNome = (a, b) => a.nome.localeCompare(b.nome, 'pt-BR');
    if (ordem === 'estoque') return filtrados.sort((a, b) => (b.estoqueGrupo - a.estoqueGrupo) || porNome(a, b));
    if (ordem === 'nome') return filtrados.sort(porNome);
    if (ordem === 'comissao') return filtrados.sort((a, b) => (b.pagaComissao - a.pagaComissao) || (b.estoqueGrupo - a.estoqueGrupo) || porNome(a, b));
    const mapa = new Map();
    for (const p of filtrados) {
      if (!mapa.has(p.fornecedor)) mapa.set(p.fornecedor, []);
      mapa.get(p.fornecedor).push(p);
    }
    for (const arr of mapa.values()) arr.sort((a, b) => (b.estoqueGrupo - a.estoqueGrupo) || porNome(a, b));
    const ord = [...mapa.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0], 'pt-BR'));
    return ord.flatMap(([, itens]) => itens);
  }, [cat, passa, ordem]);

  const totalFiltrado = filtradosOrdenados.length;
  const POR_PAGINA = 100;
  const totalPaginas = Math.max(1, Math.ceil(totalFiltrado / POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);

  // Volta pra página 1 quando o usuário MUDA o filtro (mas não no 1º load, pra
  // não atropelar a página restaurada ao voltar do detalhe). Depende dos FILTROS
  // em si — NÃO de filtradosOrdenados, que também muda quando os produtos chegam.
  const primeiroFiltro = useRef(true);
  useEffect(() => {
    if (primeiroFiltro.current) { primeiroFiltro.current = false; return; }
    setPagina(1);
  }, [busca, n2Sel, subSel, forneSel, formatoSel, dispSel, corSel, acabSel, soComEstoque, soComissao, ordem]);

  // Clampa a página ao range real depois que o catálogo carrega (ex.: página
  // restaurada da sessão maior que o total atual).
  useEffect(() => {
    if (cat && pagina > totalPaginas) setPagina(totalPaginas);
  }, [cat, totalPaginas]);

  // Só na ordem "Mais relevantes" a página é separada por fornecedor
  const grupos = useMemo(() => {
    const ini = (paginaAtual - 1) * POR_PAGINA;
    const fatia = filtradosOrdenados.slice(ini, ini + POR_PAGINA);
    if (ordem !== 'relevancia') return fatia.length ? [[null, fatia]] : [];
    const mapa = new Map();
    for (const p of fatia) {
      if (!mapa.has(p.fornecedor)) mapa.set(p.fornecedor, []);
      mapa.get(p.fornecedor).push(p);
    }
    return [...mapa.entries()];
  }, [filtradosOrdenados, paginaAtual, ordem]);

  const irPagina = (p) => { setPagina(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  // Janela de páginas a exibir (1 … atual±2 … última)
  const paginasVisiveis = () => {
    const ps = [];
    for (let i = 1; i <= totalPaginas; i++) {
      if (i === 1 || i === totalPaginas || (i >= paginaAtual - 2 && i <= paginaAtual + 2)) ps.push(i);
      else if (ps[ps.length - 1] !== '…') ps.push('…');
    }
    return ps;
  };

  // Faixas de destaque no topo — só na "vitrine" do departamento (sem nada escolhido)
  const semFiltro = !termo && !n2Sel && filtrosAtivos === 0;

  // Opções de cada filtro em ORDEM FIXA (mais produtos no departamento primeiro)
  const opcoesFiltro = useMemo(() => {
    const prods = cat?.produtos || [];
    const contar = (campo, semCat) => {
      const m = new Map();
      for (const p of prods) { const v = p[campo] || (semCat ? SEM_CAT : null); if (v) m.set(v, (m.get(v) || 0) + 1); }
      return m;
    };
    const ord = (lista, m) => [...lista].sort((a, b) => (m.get(b) || 0) - (m.get(a) || 0) || String(a).localeCompare(String(b), 'pt-BR'));
    // "Não definido" vai SEMPRE no fim (é o resto, não entra na ordenação por contagem)
    const comSem = (lista, m) => (m.get(SEM_CAT) ? [...ord(lista, m), SEM_CAT] : ord(lista, m));
    const n3 = contar('n3');
    return {
      subsDe: (n2) => ord((cat?.arvore || []).filter(a => !n2 || a.cod === n2).flatMap(a => a.filhos.map(f => f.cod)), n3),
      fornecedores: ord(cat?.fornecedores || [], contar('fornecedor')),
      formatos: ord(cat?.formatos || [], contar('formato')),
      cores: comSem(cat?.cores || [], contar('cor', true)),
      acabamentos: comSem(cat?.acabamentos || [], contar('acabamento', true)),
      disp: ord(['De Linha', 'Encomenda'], contar('disponibilidade')),
    };
  }, [cat]);

  // Contagem cruzada de cada faceta (ignora o próprio filtro da faceta)
  const cont = useMemo(() => {
    const prods = cat?.produtos || [];
    const contar = (campo, exceto, semCat) => {
      const m = new Map();
      for (const p of prods) {
        if (!passa(p, ...exceto)) continue;
        const v = p[campo] || (semCat ? SEM_CAT : null);
        if (v) m.set(v, (m.get(v) || 0) + 1);
      }
      return m;
    };
    let estoque = 0, comissao = 0;
    for (const p of prods) {
      if (Math.round(p.estoqueGrupo) > 0 && passa(p, 'estoque')) estoque++;
      if (p.pagaComissao && passa(p, 'comissao')) comissao++;
    }
    return {
      n2: contar('n2', ['n2', 'sub']), n3: contar('n3', ['sub']),
      fornecedor: contar('fornecedor', ['forne']), formato: contar('formato', ['formato']),
      disp: contar('disponibilidade', ['disp']), cor: contar('cor', ['cor'], true), acab: contar('acabamento', ['acab'], true),
      estoque, comissao,
    };
  }, [cat, passa]);

  const porCod = useMemo(() => new Map((cat?.produtos || []).map(p => [p.codpro, p])), [cat]);
  const faixaRecom = semFiltro ? (cat?.destaques || []).map(c => porCod.get(c)).filter(Boolean) : [];
  const faixaIndicados = semFiltro ? (cat?.maisIndicados || []).map(c => porCod.get(c)).filter(Boolean) : [];

  const deps = cat?.categorias || [];
  const depAtual = deps.find(d => d.cat === (cat?.categoria || categoria));
  const arvore = cat?.arvore || [];
  const soUmN2 = arvore.length === 1; // departamento de uma categoria só: pula direto pras subcategorias
  const n2Efetivo = n2Sel || (soUmN2 ? arvore[0].cod : '');
  const subsVisiveis = n2Efetivo ? opcoesFiltro.subsDe(n2Efetivo) : [];
  const tituloLista = (subSel.length === 1 && nomeClasse.get(subSel[0])) || (n2Sel && nomeClasse.get(n2Sel)) || cat?.nomeCategoria || 'Catálogo';

  // Etiquetas dos filtros ativos (cada uma remove o próprio filtro)
  const etiquetas = [
    termo && { k: 'busca', txt: `“${busca.trim()}”`, fn: () => setBusca('') },
    n2Sel && { k: 'n2', txt: nomeClasse.get(n2Sel), fn: () => escolherN2('') },
    ...subSel.map(c => ({ k: `s${c}`, txt: nomeClasse.get(c) || c, fn: () => setSubSel(s => s.filter(x => x !== c)) })),
    ...forneSel.map(v => ({ k: `f${v}`, txt: v, fn: () => setForneSel(s => s.filter(x => x !== v)) })),
    ...formatoSel.map(v => ({ k: `t${v}`, txt: v, fn: () => setFormatoSel(s => s.filter(x => x !== v)) })),
    ...corSel.map(v => ({ k: `c${v}`, txt: `Cor: ${v}`, fn: () => setCorSel(s => s.filter(x => x !== v)) })),
    ...acabSel.map(v => ({ k: `a${v}`, txt: `Acabamento: ${v}`, fn: () => setAcabSel(s => s.filter(x => x !== v)) })),
    ...dispSel.map(v => ({ k: `d${v}`, txt: v, fn: () => setDispSel(s => s.filter(x => x !== v)) })),
    soComEstoque && { k: 'est', txt: 'Com estoque', fn: () => setSoComEstoque(false) },
    soComissao && { k: 'com', txt: 'Paga comissão', fn: () => setSoComissao(false) },
  ].filter(Boolean);

  // Mega-menu: abre com um respiro ao passar o mouse (não pisca ao cruzar a barra)
  const hover = temHover();
  const entrarDep = (c) => { if (!hover) return; clearTimeout(menuTimer.current); menuTimer.current = setTimeout(() => setMenuAberto(c), 140); };
  const sairMenu = () => { clearTimeout(menuTimer.current); menuTimer.current = setTimeout(() => setMenuAberto(null), 180); };
  const manterMenu = () => clearTimeout(menuTimer.current);
  const depMenu = deps.find(d => d.cat === menuAberto);
  const estreito = typeof window !== 'undefined' && window.innerWidth < 600;

  const naEsp = (codpro) => (esp?.habilitado ? esp.itemDoProduto(codpro) : null);
  const especificar = esp?.habilitado ? (p) => esp.abrirAdicionar(p) : null;
  const card = (p, prefixo = '') => (
    <CatalogoCard key={`${prefixo}${p.codpro}`} produto={p} apiUrl={apiUrl} onAbrir={() => irProduto(p.codpro)}
      onEspecificar={especificar ? () => especificar(p) : null} naEsp={naEsp(p.codpro)} />
  );

  // Facetas — as mesmas na coluna lateral (computador) e na folha de filtros (celular)
  const facetas = (
    <>
      {arvore.length > 0 && (
        <div className="fac">
          <div className="fac-cab estatico"><span>Categorias</span></div>
          <ul className="fac-nav">
            {n2Sel && !soUmN2 && (
              <li><button type="button" className="fac-nav-voltar" onClick={() => escolherN2('')}><i className="bi bi-chevron-left" />Todas em {cat?.nomeCategoria}</button></li>
            )}
            {!n2Efetivo && arvore.map(a => (
              <li key={a.cod}>
                <button type="button" className="fac-nav-op" onClick={() => escolherN2(a.cod)} disabled={!(cont.n2.get(a.cod) || 0)}>
                  <span>{a.nome}</span><em>{cont.n2.get(a.cod) || 0}</em>
                </button>
              </li>
            ))}
            {n2Efetivo && !soUmN2 && <li className="fac-nav-atual">{nomeClasse.get(n2Efetivo)}</li>}
          </ul>
          {n2Efetivo && subsVisiveis.length > 0 && (
            <ul className="fac-subs">
              {subsVisiveis.filter(c => subSel.includes(c) || (cont.n3.get(c) || 0) > 0).map(c => (
                <li key={c}>
                  <label className={`fac-op ${subSel.includes(c) ? 'on' : ''}`}>
                    <input type="checkbox" checked={subSel.includes(c)} onChange={() => toggleEm(setSubSel)(c)} />
                    <span className="fac-op-txt">{nomeClasse.get(c)}</span>
                    <span className="fac-cont">{cont.n3.get(c) || 0}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="fac fac-chaves">
        <button type="button" role="switch" className={`cat-switch ${soComissao ? 'ativo' : ''}`} onClick={() => setSoComissao(v => !v)} aria-checked={soComissao}>
          <span className="cat-switch-track"><span className="cat-switch-thumb" /></span>
          <span className="fac-chave-txt"><i className="bi bi-cash-coin" /> Só que paga comissão</span>
          <em className="fac-cont">{cont.comissao}</em>
        </button>
        <button type="button" role="switch" className={`cat-switch ${soComEstoque ? 'ativo' : ''}`} onClick={() => setSoComEstoque(v => !v)} aria-checked={soComEstoque}>
          <span className="cat-switch-track"><span className="cat-switch-thumb" /></span>
          <span className="fac-chave-txt">Só com estoque</span>
          <em className="fac-cont">{cont.estoque}</em>
        </button>
      </div>
      <Faceta titulo="Marca" opcoes={opcoesFiltro.fornecedores} contagem={cont.fornecedor} buscavel
        selecionados={forneSel} onToggle={toggleEm(setForneSel)} onLimpar={() => setForneSel([])} />
      {opcoesFiltro.formatos.length > 0 && (
        <Faceta titulo="Tamanho" opcoes={opcoesFiltro.formatos} contagem={cont.formato} buscavel
          selecionados={formatoSel} onToggle={toggleEm(setFormatoSel)} onLimpar={() => setFormatoSel([])} />
      )}
      {/* Cor e Acabamento não cobrem 100% do catálogo: quem falta cai em "Não
          definido" (última opção) e o aviso explica. Só aparecem se há
          classificação REAL (senão sobraria um filtro só com o balde). */}
      {(cat?.cores || []).length > 0 && (
        <Faceta titulo="Cor" opcoes={opcoesFiltro.cores} contagem={cont.cor} limite={14}
          aviso="Classificada por IA a partir da foto; o que falta fica em “Não definido”."
          desenho={op => <><span className={`fac-cor ${op === SEM_CAT ? 'sem' : ''}`} style={{ background: COR_HEX[op] || 'transparent' }} />{op}</>}
          selecionados={corSel} onToggle={toggleEm(setCorSel)} onLimpar={() => setCorSel([])} />
      )}
      {(cat?.acabamentos || []).length > 0 && (
        <Faceta titulo="Acabamento" opcoes={opcoesFiltro.acabamentos} contagem={cont.acab}
          aviso="Lido da descrição do produto; o que não tem código fica em “Não definido”."
          selecionados={acabSel} onToggle={toggleEm(setAcabSel)} onLimpar={() => setAcabSel([])} />
      )}
      <Faceta titulo="Disponibilidade" opcoes={opcoesFiltro.disp} contagem={cont.disp}
        selecionados={dispSel} onToggle={toggleEm(setDispSel)} onLimpar={() => setDispSel([])} />
    </>
  );

  return (
    <div className={`loja ${mostrarHeader ? '' : 'topo-oculto'}`} style={{ '--loja-topo-h': `${topoH}px` }}>
      <div className="loja-cabeca">
        <div>
          <h1 className="cat-titulo">Catálogo de <span>Produtos</span></h1>
          <p className="loja-legenda">
            <span className="loja-legenda-ico" aria-hidden="true"><i className="bi bi-cash-coin" /></span>
            <span>Produtos com este símbolo pagam <strong>comissão por indicação</strong> quando você especifica.</span>
          </p>
        </div>
      </div>

      {/* Topo fixo: busca + especificação, e a barra de departamentos com mega-menu */}
      <div ref={topoRef} className={`loja-topo ${mostrarHeader ? '' : 'oculta'}`} onMouseLeave={sairMenu}>
        <div className="loja-barra">
          <div className="cat-busca-wrap">
            <i className="bi bi-search" />
            <input className="cat-busca" type="search" placeholder={estreito ? 'Buscar produto...' : `Buscar em ${depAtual?.nome || 'produtos'}...`}
              value={busca} onChange={e => setBusca(e.target.value)} autoComplete="off" />
            {busca && (
              <button className="cat-busca-x" onClick={() => setBusca('')} aria-label="Limpar busca" type="button"><i className="bi bi-x-lg" /></button>
            )}
          </div>
          <button type="button" className={`cat-filtros-btn loja-so-celular ${filtrosAtivos ? 'tem' : ''}`} onClick={() => setFolhaFiltros(true)}>
            <i className="bi bi-sliders2" />
            <span className="cat-filtros-btn-txt">Filtros</span>
            {filtrosAtivos > 0 && <span className="cat-filtros-badge">{filtrosAtivos}</span>}
          </button>
          <BotaoEspecificacao />
        </div>

        {deps.length > 0 && (
          <div className="loja-deps-linha">
            {/* Fica FIXO: não rola junto com os departamentos */}
            <button type="button" className={`loja-dep todos ${menuAberto === '__todos' ? 'menu' : ''}`}
              onMouseEnter={() => entrarDep('__todos')}
              onClick={() => (hover ? setMenuAberto(m => (m === '__todos' ? null : '__todos')) : setFolhaDeps(true))}>
              <i className="bi bi-list" />Departamentos
            </button>
            {(setas.esq || setas.dir) && (
              <button type="button" className="loja-seta" onClick={() => rolarDeps(-1)} disabled={!setas.esq} aria-label="Ver departamentos anteriores">
                <i className="bi bi-chevron-left" />
              </button>
            )}
          <nav ref={depsRef} className="loja-deps" aria-label="Departamentos" onScroll={medirSetas}>
            {deps.map(d => (
              <button key={d.cat} type="button"
                className={`loja-dep ${d.cat === (cat?.categoria || categoria) ? 'ativa' : ''} ${d.ativo ? '' : 'previa'} ${menuAberto === d.cat ? 'menu' : ''}`}
                onMouseEnter={() => entrarDep(d.cat)}
                onClick={() => navegar({ cat: d.cat })}
                title={d.ativo ? undefined : 'Departamento desligado — só quem gerencia o catálogo vê'}>
                {d.nome}
                {!d.ativo && <span className="cat-chip-previa">prévia</span>}
              </button>
            ))}
          </nav>
            {(setas.esq || setas.dir) && (
              <button type="button" className="loja-seta" onClick={() => rolarDeps(1)} disabled={!setas.dir} aria-label="Ver mais departamentos">
                <i className="bi bi-chevron-right" />
              </button>
            )}
          </div>
        )}

        {depMenu && depMenu.categorias.length > 0 && (
          <div className="mega" onMouseEnter={manterMenu} onMouseLeave={sairMenu}>
            <div className="mega-cab">
              <button type="button" onClick={() => navegar({ cat: depMenu.cat })}>
                Ver tudo em {depMenu.nome} <span>{depMenu.qtd.toLocaleString('pt-BR')} produtos</span><i className="bi bi-arrow-right" />
              </button>
            </div>
            <div className="mega-cols">
              {depMenu.categorias.map(c2 => (
                <div key={c2.cod} className="mega-col">
                  <button type="button" className="mega-n2" onClick={() => navegar({ cat: depMenu.cat, n2: c2.cod })}>{c2.nome}</button>
                  <ul>
                    {c2.filhos.slice(0, 7).map(c3 => (
                      <li key={c3.cod}>
                        <button type="button" onClick={() => navegar({ cat: depMenu.cat, n2: c2.cod, n3: c3.cod })}>
                          {c3.nome}<span>{c3.qtd}</span>
                        </button>
                      </li>
                    ))}
                    {c2.filhos.length > 7 && (
                      <li><button type="button" className="mega-mais" onClick={() => navegar({ cat: depMenu.cat, n2: c2.cod })}>Ver todas ({c2.filhos.length})</button></li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {menuAberto === '__todos' && (
          <div className="mega" onMouseEnter={manterMenu} onMouseLeave={sairMenu}>
            <div className="mega-cols">
              {deps.map(d => (
                <div key={d.cat} className="mega-col">
                  <button type="button" className="mega-n2" onClick={() => navegar({ cat: d.cat })}>
                    {d.nome}{!d.ativo && <span className="cat-chip-previa">prévia</span>}
                  </button>
                  <ul>
                    {d.categorias.slice(0, 5).map(c2 => (
                      <li key={c2.cod}>
                        <button type="button" onClick={() => navegar({ cat: d.cat, n2: c2.cod })}>{c2.nome}<span>{c2.qtd}</span></button>
                      </li>
                    ))}
                    {d.categorias.length > 5 && (
                      <li><button type="button" className="mega-mais" onClick={() => navegar({ cat: d.cat })}>Ver todas ({d.categorias.length})</button></li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Celular: categorias do departamento em chips */}
        {arvore.length > 1 && (
          <div className="cat-subchips loja-so-celular">
            <button type="button" className={`cat-subchip ${!n2Sel ? 'ativa' : ''}`} onClick={() => escolherN2('')}>Todas</button>
            {arvore.map(a => (
              <button key={a.cod} type="button" className={`cat-subchip ${n2Sel === a.cod ? 'ativa' : ''}`} onClick={() => escolherN2(a.cod)}>
                {a.nome}<span className="cont">{a.qtd}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Trilha: Catálogo › Departamento › Categoria › Subcategoria */}
      {cat && n2Sel && (
        <nav className="loja-trilha" aria-label="Você está em">
          <button type="button" onClick={() => { escolherN2(''); }}>{cat.nomeCategoria}</button>
          {n2Sel && <><i className="bi bi-chevron-right" /><button type="button" onClick={() => setSubSel([])}>{nomeClasse.get(n2Sel)}</button></>}
          {subSel.length === 1 && <><i className="bi bi-chevron-right" /><span>{nomeClasse.get(subSel[0])}</span></>}
        </nav>
      )}

      <div className="loja-corpo">
        <aside className="loja-lateral" aria-label="Filtros">
          {cat && facetas}
          {filtrosAtivos > 0 && <button type="button" className="fac-limpar-tudo" onClick={limparFiltros}><i className="bi bi-x-circle" />Limpar filtros</button>}
        </aside>

        <section className="loja-lista">
          <div className="loja-lista-cab">
            <div className="loja-lista-tit">
              <h2>{tituloLista}</h2>
              {!carregando && !erro && <span>{totalFiltrado.toLocaleString('pt-BR')} produto{totalFiltrado === 1 ? '' : 's'}</span>}
            </div>
            <label className="loja-ordem">
              <span>Ordenar</span>
              <select value={ordem} onChange={e => setOrdem(e.target.value)}>
                {ORDENS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </label>
          </div>

          {etiquetas.length > 0 && (
            <div className="loja-etiquetas">
              {etiquetas.map(e => (
                <button key={e.k} type="button" className="loja-etq" onClick={e.fn}>{e.txt}<i className="bi bi-x" /></button>
              ))}
              {etiquetas.length > 1 && <button type="button" className="loja-etq-limpar" onClick={limparTudo}>Limpar tudo</button>}
            </div>
          )}

          {carregando ? (
            <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} /></div>
          ) : erro ? (
            <div className="alert alert-danger">{erro}</div>
          ) : grupos.length === 0 ? (
            <div className="cat-vazio">
              <i className="bi bi-search" />
              <div>Nenhum produto com esses filtros.</div>
              {etiquetas.length > 0 && <button type="button" className="esp-btn-sec mt-3" onClick={limparTudo}>Limpar filtros</button>}
            </div>
          ) : (
            <>
              {faixaRecom.length > 0 && (
                <section>
                  <div className="cat-secao-tit cat-secao-destaque">
                    <i className="bi bi-star-fill" /> Recomendados<span className="cont">{faixaRecom.length}</span>
                  </div>
                  <div className="cat-grid">{faixaRecom.map(p => card(p, 'r-'))}</div>
                </section>
              )}
              {faixaIndicados.length > 0 && (
                <section>
                  <div className="cat-secao-tit cat-secao-destaque">
                    <i className="bi bi-hand-thumbs-up-fill" /> Mais indicados<span className="cont">{faixaIndicados.length}</span>
                  </div>
                  <div className="cat-grid">{faixaIndicados.map(p => card(p, 'i-'))}</div>
                </section>
              )}
              {grupos.map(([fornecedor, itens]) => (
                <section key={fornecedor || 'lista'}>
                  {fornecedor !== null
                    ? <div className="cat-secao-tit">{fornecedor}<span className="cont">{itens.length}</span></div>
                    : (faixaRecom.length > 0 || faixaIndicados.length > 0) && <div className="cat-secao-tit">Todos os produtos</div>}
                  <div className="cat-grid">{itens.map(p => card(p))}</div>
                </section>
              ))}
              {/* Paginação numerada (100 por página) */}
              {totalPaginas > 1 && (
                <div className="cat-paginacao">
                  <button className="cat-pag-btn" disabled={paginaAtual <= 1} onClick={() => irPagina(paginaAtual - 1)} aria-label="Página anterior"><i className="bi bi-chevron-left" /></button>
                  {paginasVisiveis().map((p, i) => p === '…'
                    ? <span key={`e${i}`} className="cat-pag-sep">…</span>
                    : <button key={p} className={`cat-pag-btn ${p === paginaAtual ? 'ativa' : ''}`} onClick={() => irPagina(p)}>{p}</button>
                  )}
                  <button className="cat-pag-btn" disabled={paginaAtual >= totalPaginas} onClick={() => irPagina(paginaAtual + 1)} aria-label="Próxima página"><i className="bi bi-chevron-right" /></button>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Celular: filtros numa folha em tela cheia, com o total ao vivo no botão */}
      {folhaFiltros && (
        <Folha
          titulo="Filtros"
          subtitulo={cat?.nomeCategoria}
          onFechar={() => setFolhaFiltros(false)}
          className="folha-filtros"
          rodape={(
            <>
              <button type="button" className="esp-btn-sec" onClick={limparFiltros} disabled={!filtrosAtivos}>Limpar</button>
              <button type="button" className="esp-btn" onClick={() => setFolhaFiltros(false)}>
                Ver {totalFiltrado.toLocaleString('pt-BR')} produto{totalFiltrado === 1 ? '' : 's'}
              </button>
            </>
          )}
        >
          {facetas}
        </Folha>
      )}

      {/* Celular: todos os departamentos e as categorias de cada um */}
      {folhaDeps && (
        <Folha titulo="Departamentos" onFechar={() => setFolhaDeps(false)}>
          <div className="deps-lista">
            {deps.map(d => (
              <details key={d.cat} className="deps-item" open={d.cat === (cat?.categoria || categoria)}>
                <summary>
                  <span>{d.nome}{!d.ativo && <span className="cat-chip-previa">prévia</span>}</span>
                  <em>{d.qtd.toLocaleString('pt-BR')}</em>
                </summary>
                <button type="button" className="deps-tudo" onClick={() => { setFolhaDeps(false); navegar({ cat: d.cat }); }}>
                  Ver tudo em {d.nome}<i className="bi bi-arrow-right" />
                </button>
                {d.categorias.map(c2 => (
                  <button key={c2.cod} type="button" className="deps-n2" onClick={() => { setFolhaDeps(false); navegar({ cat: d.cat, n2: c2.cod }); }}>
                    {c2.nome}<span>{c2.qtd}</span>
                  </button>
                ))}
              </details>
            ))}
          </div>
        </Folha>
      )}

      {mostrarHeader && scrollY > 400 && (
        <button className={`cat-voltar-topo ${esp?.ativa ? 'com-bolha' : ''}`} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} title="Voltar ao topo" aria-label="Voltar ao topo">
          <i className="bi bi-arrow-up" />
        </button>
      )}
    </div>
  );
}
